'use client';

import { Loader2 } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import { useState } from 'react';
import { Icon } from '@/lib/ui-kit/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { formatMoney } from '@/lib/utils/formatAmount';
import { BUTTON_M_CLASS, LABEL_CLASS, TOOLTIP_CONTENT_CLASS, Z_OVERLAY_MAX } from '../constants';
import type { PriceAlertCondition } from '../types';
import { isOpenInterestCondition, isWhaleCondition, parseUsdToCents } from '../utils';

function formatUsdRaw(cents: number): string {
  return formatMoney(cents / 100);
}

interface TargetVolumeFieldProps {
  valueCents: number;
  onChangeCents: (cents: number) => void;
  currentVolume: number;
  condition: PriceAlertCondition;
  isLoading?: boolean;
}

export function TargetVolumeField({
  valueCents,
  onChangeCents,
  currentVolume,
  condition,
  isLoading = false,
}: TargetVolumeFieldProps) {
  const tf = useTranslations('priceAlerts.form');
  const format = useFormatter();
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const display = focused ? draft : formatUsdRaw(valueCents);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDraft(raw);
    const cents = parseUsdToCents(raw);
    if (cents !== null) onChangeCents(cents);
  };

  const handleFocus = () => {
    setDraft((valueCents / 100).toFixed(2));
    setFocused(true);
  };
  const handleBlur = () => setFocused(false);

  const isOi = isOpenInterestCondition(condition);
  const isWhale = isWhaleCondition(condition);
  const labelKey = isWhale ? 'targetTradeSize' : isOi ? 'targetOpenInterest' : 'targetVolume';
  const label = tf(labelKey);
  const currentLabel = isWhale ? tf('largestSoFar') : tf('current');
  const tooltipKey = isWhale ? 'whale' : isOi ? 'openInterest' : 'volume';
  const tooltipText = tf(`currentTooltip.${tooltipKey}`);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className={LABEL_CLASS}>{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={tooltipText}
              className="group flex cursor-pointer items-center gap-0.5 text-[13px] leading-4 font-normal outline-none"
            >
              <span className="text-v2-text-primary opacity-50">{currentLabel}</span>
              {isLoading ? (
                <Loader2 className="ml-1 size-3.5 animate-spin text-v2-text-secondary" />
              ) : (
                <span className="text-v2-text-primary">
                  &nbsp;{format.number(currentVolume, { style: 'currency', currency: 'USD' })}
                </span>
              )}
              <Icon
                name="Attention"
                size={12}
                className="ml-1 text-v2-text-secondary transition-colors group-hover:text-v2-text-primary"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="end"
            showArrow={false}
            style={{ textWrap: 'wrap', zIndex: Z_OVERLAY_MAX }}
            className={TOOLTIP_CONTENT_CLASS}
          >
            {tooltipText}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex h-11 items-center justify-center rounded-[10px] bg-v2-btn-secondary px-4">
        {isLoading ? (
          <Loader2 className="size-5 animate-spin text-v2-text-secondary" />
        ) : (
          <>
            <span className={cn('text-v2-text-primary', BUTTON_M_CLASS)}>$</span>
            <input
              type="text"
              inputMode="decimal"
              value={display}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              aria-label={label}
              size={Math.max(display.length, 1)}
              className={cn(
                'min-w-0 bg-transparent text-left text-v2-text-primary outline-none placeholder:text-v2-text-secondary',
                BUTTON_M_CLASS,
              )}
            />
          </>
        )}
      </div>
    </div>
  );
}
